import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { SECTOR_MAP } from '../../utils/sectorMap';

const COLORS = ['#6495ED', '#10B981', '#FBBF24', '#7B68EE', '#00B4D8', '#F97316', '#EC4899', '#8B5CF6', '#06B6D4', '#84CC16', '#14B8A6', '#F43F5E', '#A855F7', '#EAB308'];

// Sector table lives in utils/sectorMap.js (shared with the portfolio heatmap).

export default function SectorDonut({ scores }) {
  if (!scores?.length) return null;

  const sectorData = {};
  scores.forEach(s => {
    // Use sector from analysis data if available, otherwise fall back to mapping
    const sector = s.sector || SECTOR_MAP[s.symbol] || 'Other';
    if (!sectorData[sector]) sectorData[sector] = 0;
    sectorData[sector] += s.position_value || 0;
  });

  const data = Object.entries(sectorData)
    .map(([name, value]) => ({ name, value: Math.round(value) }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  if (data.length === 0) return null;

  const total = data.reduce((s, d) => s + d.value, 0);

  // Only label sectors > 8% to avoid overlap
  const renderLabel = ({ name, percent, cx, cy, midAngle, outerRadius }) => {
    if (percent < 0.08) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#94A3B8" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={10}>
        {name} {(percent * 100).toFixed(0)}%
      </text>
    );
  };

  return (
    <div style={{ width: '100%', maxWidth: 420 }}>
      <h4 style={{ textAlign: 'center', color: '#94A3B8', fontSize: '0.85rem', margin: '0 0 8px' }}>Sector Allocation</h4>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value" label={renderLabel} labelLine={false}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip
            formatter={(value) => `$${value.toLocaleString()}`}
            contentStyle={{ background: '#1A1F2E', border: '1px solid #334155', borderRadius: 8, color: '#F1F5F9' }}
            itemStyle={{ color: '#F1F5F9' }}
            labelStyle={{ color: '#94A3B8' }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.35rem 0.75rem', marginTop: '0.25rem' }}>
        {data.map((d, i) => (
          <span key={d.name} style={{ fontSize: '0.65rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[i % COLORS.length], display: 'inline-block' }} />
            {d.name} ({(d.value / total * 100).toFixed(0)}%)
          </span>
        ))}
      </div>
    </div>
  );
}
